/**
 * Supabase Client
 * Singleton instance for Supabase connection
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js'
import { config } from './config'

/**
 * Database Types
 * These will be auto-generated from Supabase CLI in Phase 3.
 * For now, we define them manually to match our schema design.
 */

// Shipping address structure
export interface ShippingAddress {
  street?: string
  city?: string
  state?: string
  zip?: string
  country?: string
}

// Profile row type
export interface ProfileRow {
  id: string
  username: string
  display_name: string | null
  bio: string | null
  phone_number: string | null
  birthdate: string | null
  shipping_address: ShippingAddress | null
  is_admin: boolean
  is_approved: boolean
  created_at: string
  updated_at: string | null
  username_updated_at: string | null
}

// Insight row type
export interface InsightRow {
  id: string
  user_id: string
  content: string
  sentiment: 'bullish' | 'bearish' | 'neutral'
  ticker: string | null
  tags: string[]
  image_url: string | null
  created_at: string
  updated_at: string | null
  track_count: number | null
}

// Insight with joined profile data
export interface InsightWithProfile extends InsightRow {
  profiles: {
    username: string
    display_name: string | null
    is_admin: boolean
  } | null
}

// Singleton instance (untyped for flexibility until schema is finalized)
let supabaseInstance: SupabaseClient | null = null

/**
 * Get Supabase client instance
 * Returns null if Supabase is not configured (mock mode)
 */
export function getSupabaseClient(): SupabaseClient | null {
  // Return null if not using backend or missing config
  if (!config.useBackend) {
    return null
  }

  if (!config.supabase.url || !config.supabase.anonKey) {
    console.warn(
      'Supabase URL or Anon Key not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
    )
    return null
  }

  // Create singleton instance
  if (!supabaseInstance) {
    supabaseInstance = createClient(
      config.supabase.url,
      config.supabase.anonKey,
      {
        auth: {
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: true,
        },
      }
    )
  }

  return supabaseInstance
}

/**
 * Get Supabase client or throw error
 * Use this when Supabase is required
 */
export function requireSupabaseClient(): SupabaseClient {
  const client = getSupabaseClient()
  if (!client) {
    throw new Error(
      'Supabase client not available. Ensure VITE_USE_BACKEND=true and Supabase credentials are configured.'
    )
  }
  return client
}

// Export type for use in repositories
export type { SupabaseClient }
